import { NoddiInput } from 'noddi-ui-web';
import {
  CountryCode,
  CountryCodeType,
  getAcceptedLicensePlateNumberLengths,
  isValidLicensePlateNumber
} from 'noddi-util';
import { KeyboardEvent, Ref } from 'react';

import NorwegianLicensePlateImg from './norwegian_license_plate.png';
import SwedishLicensePlateImg from './swedish_license_plate.png';

type Props = {
  value: string;
  /** Receives the input trimmed and upper-cased, the form every plate lookup expects. */
  onChange: (value: string) => void;
  placeholder: string;
  countryCode?: CountryCodeType;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  ref?: Ref<HTMLInputElement>;
};

/**
 * License plate field with the country plate badge, upper-casing and the plate regex. Shared by
 * every place a user types a plate so they all look and validate the same.
 */
export const LicensePlateInput = ({
  value,
  onChange,
  placeholder,
  countryCode = CountryCode.NORWAY,
  onKeyDown,
  ref
}: Props) => {
  const isInvalid = !isValidLicensePlateNumber({ input: value, countryCode });
  return (
    <NoddiInput
      wrapperClassName='w-full'
      className='min-w-0 overflow-hidden'
      ref={ref}
      placeholder={placeholder}
      aria-label={placeholder}
      aria-invalid={isInvalid || undefined}
      value={value}
      onChange={(event) => onChange(event.target.value.trim().toUpperCase())}
      onKeyDown={onKeyDown}
      maxLength={Math.max(...getAcceptedLicensePlateNumberLengths({ countryCode }))}
      startAdornment={
        <img
          src={getLicensePlateImageSource(countryCode)}
          alt={getLicensePlateAltTag(countryCode)}
          className='-ml-3 h-10 w-9'
        />
      }
    />
  );
};

const getLicensePlateImageSource = (countryCode: CountryCodeType) => {
  if (countryCode === CountryCode.NORWAY) {
    return NorwegianLicensePlateImg;
  } else if (countryCode === CountryCode.SWEDEN) {
    return SwedishLicensePlateImg;
  }

  throw new Error(`Unsupported country code: ${countryCode}`);
};

const getLicensePlateAltTag = (countryCode: CountryCodeType) => {
  if (countryCode === CountryCode.NORWAY) {
    return 'Norwegian license plate';
  } else if (countryCode === CountryCode.SWEDEN) {
    return 'Swedish license plate';
  }

  throw new Error(`Unsupported country code: ${countryCode}`);
};
